/** Terminal authentication failure. Retrying cannot recover this session. */
export class SessionExpiredError extends Error {
  readonly status = 401;
  readonly retryable = false;

  constructor() {
    super('Session expired');
    this.name = 'SessionExpiredError';
  }
}

/**
 * The request went out with no credentials at all, and the server said 401.
 *
 * Distinct from `SessionExpiredError`, which means credentials were sent and
 * are dead. This one says nothing about the stored session: an anonymous
 * visitor on a `/share/...` route produces it, and so does a cold boot whose
 * request beats the Stytch JWT cookie into existence — the SDK's constructor
 * kicks off a background refresh that writes that cookie asynchronously, so
 * `getSessionJwt()` can be empty for the first request of a page load while a
 * perfectly good session is on its way. Ending the session on one of these
 * revoked a live session and stranded the user on `/login` (ENG-1887).
 *
 * Not retryable: no amount of retrying conjures a credential the caller does
 * not have, and the boot case is refetched by the component behind the loader.
 */
export class UnauthenticatedError extends Error {
  readonly status = 401;
  readonly retryable = false;

  constructor() {
    super('Not authenticated');
    this.name = 'UnauthenticatedError';
  }
}

type SessionExpiryHandler = () => void | Promise<void>;

let handler: SessionExpiryHandler | null = null;
let transition: Promise<void> | null = null;
let requested = false;

function startRegisteredTransition(): boolean {
  if (!handler) return false;
  if (!transition) {
    const currentHandler = handler;
    transition = Promise.resolve()
      .then(currentHandler)
      // Logout owns reporting and navigation. Keep terminal failure from
      // becoming an unhandled rejection while document unloads.
      .catch(() => undefined);
  }
  return true;
}

/** Registers current provider's logout action as terminal expiry transition. */
export function registerSessionExpiryHandler(
  nextHandler: SessionExpiryHandler,
): () => void {
  handler = nextHandler;
  if (requested) startRegisteredTransition();
  return () => {
    if (handler === nextHandler) handler = null;
  };
}

/** Starts provider logout + hard login navigation once for current page load. */
export function beginSessionExpiryTransition(): boolean {
  requested = true;
  return startRegisteredTransition();
}

export function isSessionExpiryTransitionStarted(): boolean {
  return requested;
}

export function isSessionExpiredError(
  error: unknown,
): error is SessionExpiredError {
  return error instanceof SessionExpiredError;
}

export function isUnauthenticatedError(
  error: unknown,
): error is UnauthenticatedError {
  return error instanceof UnauthenticatedError;
}

/**
 * TanStack retry predicate preserving its browser default of three retries.
 *
 * Both auth failures are excluded. Retrying either just repeats a request that
 * cannot succeed — and because route loaders `await` these queries, three
 * attempts at TanStack's default backoff would park the first navigation on a
 * spinner for seconds before the component behind it gets to render.
 */
export function retryUnlessSessionExpired(
  failureCount: number,
  error: unknown,
  maxRetries = 3,
): boolean {
  if (isSessionExpiredError(error) || isUnauthenticatedError(error)) {
    return false;
  }
  return failureCount < maxRetries;
}

/** Test isolation for module-scoped terminal state. */
export function resetSessionExpiryStateForTests(): void {
  handler = null;
  transition = null;
  requested = false;
}
